import {
  ConnectionType,
  MarketAccountTypeEnum,
  useConnectionStatusContext,
  useMarketAccountsContext,
  useMarketsContext,
  type Market,
  type MarketAccount,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { getMarketAccountActiveCondition } from '../../MarketSelector/conditions/getMarketAccountActiveCondition';
import { getMarketConfiguredCondition } from '../../MarketSelector/conditions/useMarketConfiguredCondition';
import { useBuildMarketSelectorItems } from '../../MarketSelector/utils/useBuildMarketSelectorItems';

export function getPriceGridMarketSelectorMarketsAndAccounts({
  marketsList,
  marketAccountsByMarket,
}: {
  marketsList: Market[];
  marketAccountsByMarket: Map<string, MarketAccount[]>;
}) {
  const markets: Market[] = [];
  const marketAccounts: MarketAccount[] = [];

  for (const market of marketsList) {
    const tradingMarketAccounts = marketAccountsByMarket
      .get(market.Name)
      ?.filter(marketAccount => marketAccount.Type === MarketAccountTypeEnum.Trading);

    if (tradingMarketAccounts && tradingMarketAccounts.length > 0) {
      marketAccounts.push(...tradingMarketAccounts);
    } else {
      markets.push(market);
    }
  }

  return { markets, marketAccounts };
}

export function usePriceGridMarketSelectorItems() {
  const { marketsList } = useMarketsContext();
  const { marketAccountsByMarket } = useMarketAccountsContext();
  const { marketCredentialsWithConnectionStatusByMarketAccountName } = useConnectionStatusContext();

  // Keep conditions referentially stable so column dynamics specs (and columnDefs) are not rebuilt every render.
  // useMarketDataMarketSelectorConditions returns a new array each time and caused price column settings to revert.
  const conditions = useMemo(
    () => [
      getMarketConfiguredCondition({ connectionType: ConnectionType.MarketData }),
      getMarketAccountActiveCondition({
        disableIfTradingDisabled: false,
        marketCredentialsWithConnectionStatusByMarketAccountName,
      }),
    ],
    [marketCredentialsWithConnectionStatusByMarketAccountName]
  );

  const { markets, marketAccounts } = useMemo(
    () => getPriceGridMarketSelectorMarketsAndAccounts({ marketsList, marketAccountsByMarket }),
    [marketAccountsByMarket, marketsList]
  );

  return useBuildMarketSelectorItems({
    markets,
    marketAccounts,
    conditions,
    readyToBuildItems: true,
  });
}
